import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Chip,
    Avatar,
    alpha,
    useTheme,
    InputAdornment,
    CircularProgress,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    MenuItem as MuiMenuItem,
    Alert,
    Tabs,
    Tab,
    Card,
    CardContent,
    Stack,
    Divider,
    FormControl,
    Select,
    InputLabel,
    MenuItem,
} from '@mui/material';
import {
    Search,
    AccountBalanceWallet,
    AccountBalance,
    ContentCopy,
    Send,
    CheckCircle,
    Pending,
    Schedule,
    TrendingUp,
    Visibility,
} from '@mui/icons-material';
import { apiService } from '../services/api.service';
import { firebaseService } from '../services/firebase.service';
import { User, PaginationResult, WithdrawalRequest } from '../types';
import Layout from '../components/Layout';
import { getAvatarUrl } from '../utils/user';

const Payouts: React.FC = () => {
    const theme = useTheme();
    const [withdrawals, setWithdrawals] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');

    // Payout Dialog State
    const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<(WithdrawalRequest & { tutor?: User }) | null>(null);
    const [payoutAmount, setPayoutAmount] = useState('');
    const [payoutRef, setPayoutRef] = useState('');
    const [payoutStatus, setPayoutStatus] = useState<'released' | 'inProgress' | 'success'>('released');
    const [payoutAdminNotes, setPayoutAdminNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // View Payout Dialog State
    const [viewDialogOpen, setViewDialogOpen] = useState(false);

    const [statusTab, setStatusTab] = useState(0); // 0: All, 1: Requested, 2: Initiated, 3: In Progress, 4: Completed
    const [timeFilter, setTimeFilter] = useState('all'); // all, daily, weekly, monthly, custom

    // Reset page when filters change
    useEffect(() => {
        setPage(0);
    }, [statusTab, timeFilter, search]);

    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [stats, setStats] = useState({
        requested: { count: 0, amount: 0 },
        initiated: { count: 0, amount: 0 },
        inProgress: { count: 0, amount: 0 },
        completed: { count: 0, amount: 0 },
    });

    const loadWithdrawals = useCallback(async () => {
        setLoading(true);
        try {
            // Mapping tab to status
            const statusMap: Record<number, string | null> = {
                0: null, // All
                1: 'requested',
                2: 'released',
                3: 'inProgress',
                4: 'success'
            };

            const statusFilter = statusMap[statusTab];

            // Build filters for API
            const filters: any = {
                page: page + 1,
                limit: 1000, // Fetch more to handle client-side time filtering and stats
            };

            if (statusFilter) {
                filters.status = statusFilter;
            }

            const result: PaginationResult<WithdrawalRequest> = await apiService.getWithdrawals(filters);

            // 1. Filter by time range
            let filteredByTime = result.data;
            const now = new Date();

            if (timeFilter !== 'all') {
                filteredByTime = result.data.filter(w => {
                    const reqDate = firebaseService.convertTimestamp(w.requestedAt);
                    if (timeFilter === 'daily') {
                        return reqDate.toDateString() === now.toDateString();
                    } else if (timeFilter === 'weekly') {
                        const weekAgo = new Date();
                        weekAgo.setDate(now.getDate() - 7);
                        return reqDate >= weekAgo;
                    } else if (timeFilter === 'monthly') {
                        return reqDate.getMonth() === now.getMonth() && reqDate.getFullYear() === now.getFullYear();
                    } else if (timeFilter === 'custom' && customStartDate && customEndDate) {
                        const start = new Date(customStartDate);
                        const end = new Date(customEndDate);
                        end.setHours(23, 59, 59, 999);
                        return reqDate >= start && reqDate <= end;
                    }
                    return true;
                });
            }

            // 2. Calculate Stats from the time-filtered data (but ignore status filter for stats)
            // Wait, if result.data was already filtered by status at the API level, we can't calculate stats for OTHER statuses.
            // So we must fetch ALL statuses for stats, OR make multiple calls.
            // Let's fetch ALL statuses initially if we want accurate global stats for the time period.

            let allForStats = result.data;
            if (statusFilter) {
                // If we applied a status filter, we need another fetch or just fetch all statuses and filter in JS.
                // Fetching all is easier for stats.
                const allResult = await apiService.getWithdrawals({ page: 1, limit: 1000 });
                allForStats = allResult.data;
            }

            // Re-apply time filter to the all-status data for accurate stats
            const statsData = timeFilter === 'all' ? allForStats : allForStats.filter(w => {
                const reqDate = firebaseService.convertTimestamp(w.requestedAt);
                if (timeFilter === 'daily') return reqDate.toDateString() === now.toDateString();
                if (timeFilter === 'weekly') { const weekAgo = new Date(); weekAgo.setDate(now.getDate() - 7); return reqDate >= weekAgo; }
                if (timeFilter === 'monthly') return reqDate.getMonth() === now.getMonth() && reqDate.getFullYear() === now.getFullYear();
                if (timeFilter === 'custom' && customStartDate && customEndDate) {
                    const start = new Date(customStartDate); const end = new Date(customEndDate); end.setHours(23, 59, 59, 999);
                    return reqDate >= start && reqDate <= end;
                }
                return true;
            });

            const newStats = {
                requested: { count: 0, amount: 0 },
                initiated: { count: 0, amount: 0 },
                inProgress: { count: 0, amount: 0 },
                completed: { count: 0, amount: 0 },
            };

            statsData.forEach(w => {
                if (w.status === 'requested' || w.status === 'pending') {
                    newStats.requested.count++;
                    newStats.requested.amount += w.amount;
                } else if (w.status === 'released') {
                    newStats.initiated.count++;
                    newStats.initiated.amount += w.amount;
                } else if (w.status === 'inProgress') {
                    newStats.inProgress.count++;
                    newStats.inProgress.amount += w.amount;
                } else if (w.status === 'success') {
                    newStats.completed.count++;
                    newStats.completed.amount += w.amount;
                }
            });
            setStats(newStats);

            // 3. Enrich the Paginated/Filtered data
            // For the table, we use the data that respects BOTH status and time filters
            const tableData = statusFilter ? filteredByTime : statsData;

            // Enrich only the current page's slice (simulating pagination manually since we fetched 1000)
            const startIndex = page * rowsPerPage;
            const slice = tableData.slice(startIndex, startIndex + rowsPerPage);

            const enriched: any[] = await Promise.all(slice.map(async (req) => {
                try {
                    const tutor = await apiService.getUser(req.tutorId);
                    return { ...req, tutor };
                } catch (e) {
                    console.error(`Failed to fetch tutor ${req.tutorId}:`, e);
                    return req;
                }
            }));

            // Search filter (last step)
            const filtered = search
                ? enriched.filter((w: any) =>
                    w.tutor?.tutorProfile?.name?.toLowerCase().includes(search.toLowerCase()) ||
                    w.tutor?.email?.toLowerCase().includes(search.toLowerCase()) ||
                    w.id.toLowerCase().includes(search.toLowerCase())
                )
                : enriched;

            setWithdrawals(filtered);
            setTotal(tableData.length);
        } catch (error) {
            console.error('Failed to load withdrawals:', error);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, statusTab, timeFilter, customStartDate, customEndDate]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            loadWithdrawals();
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [loadWithdrawals]);

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        // Could add a toast notification here if available
    };

    const handleOpenPayout = (request: any) => {
        setSelectedRequest(request);
        setPayoutAmount(request.amount.toString());
        setPayoutRef(request.transactionReference || '');

        // Map pending/requested to released by default for processing
        if (request.status === 'pending' || request.status === 'requested') {
            setPayoutStatus('released');
        } else {
            setPayoutStatus(request.status);
        }

        setPayoutAdminNotes(request.adminNotes || '');
        setPayoutDialogOpen(true);
    };

    const handleClosePayout = () => {
        setPayoutDialogOpen(false);
        setSelectedRequest(null);
        setPayoutAmount('');
        setPayoutRef('');
        setPayoutAdminNotes('');
    };

    const handleProcessPayout = async () => {
        if (!selectedRequest) return;
        const amount = parseFloat(payoutAmount);
        if (isNaN(amount) || amount <= 0) {
            alert('Please enter a valid amount');
            return;
        }

        setIsSubmitting(true);
        try {
            console.log('Updating payout:', selectedRequest.id, {
                status: payoutStatus,
                payoutRef,
                payoutAdminNotes
            });

            // Update the existing withdrawal request
            const updateData: any = {
                status: payoutStatus,
                transactionReference: payoutRef,
                adminNotes: payoutAdminNotes,
                updatedAt: new Date(),
            };

            if (payoutStatus === 'success') {
                updateData.processedAt = new Date();
            } else if (selectedRequest.processedAt) {
                updateData.processedAt = selectedRequest.processedAt;
            }

            await apiService.updateWithdrawalRequest(selectedRequest.id, updateData);

            console.log('Payout updated successfully');
            alert('Payout status updated successfully');
            handleClosePayout();
            loadWithdrawals();
        } catch (error: any) {
            console.error('Failed to update payout:', error);
            alert(`Failed to update payout: ${error.message || 'Unknown error'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Layout>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
                <Box sx={{
                    mb: 4,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 3
                }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: -1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                            Partner Payouts
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                            Manage partner withdrawal requests and process payouts with manual transfers.
                        </Typography>
                    </Box>

                    {/* Time Filter Controls */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Time Range</InputLabel>
                            <Select
                                value={timeFilter}
                                label="Time Range"
                                onChange={(e) => setTimeFilter(e.target.value)}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="all">All Time</MenuItem>
                                <MenuItem value="daily">Today</MenuItem>
                                <MenuItem value="weekly">This Week</MenuItem>
                                <MenuItem value="monthly">This Month</MenuItem>
                                <MenuItem value="custom">Custom Range</MenuItem>
                            </Select>
                        </FormControl>

                        {timeFilter === 'custom' && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField
                                    type="date"
                                    size="small"
                                    label="From"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                <Typography variant="caption" color="text.secondary">to</Typography>
                                <TextField
                                    type="date"
                                    size="small"
                                    label="To"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Summary Cards */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                    gap: 3,
                    mb: 4
                }}>
                    <Card sx={{ borderRadius: 4, boxShadow: theme.shadows[1], border: '1px solid', borderColor: alpha(theme.palette.divider, 0.1) }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Box>
                                    <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Requested</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>₹{stats.requested.amount.toLocaleString()}</Typography>
                                    <Typography variant="caption" color="text.secondary">{stats.requested.count} active requests</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main' }}><Pending /></Avatar>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card sx={{ borderRadius: 4, boxShadow: theme.shadows[1], border: '1px solid', borderColor: alpha(theme.palette.divider, 0.1) }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Box>
                                    <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Initiated</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>₹{stats.initiated.amount.toLocaleString()}</Typography>
                                    <Typography variant="caption" color="text.secondary">{stats.initiated.count} payouts sent</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.main' }}><Schedule /></Avatar>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card sx={{ borderRadius: 4, boxShadow: theme.shadows[1], border: '1px solid', borderColor: alpha(theme.palette.divider, 0.1) }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Box>
                                    <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>In Progress</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>₹{stats.inProgress.amount.toLocaleString()}</Typography>
                                    <Typography variant="caption" color="text.secondary">{stats.inProgress.count} being processed</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}><TrendingUp /></Avatar>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card sx={{ borderRadius: 4, boxShadow: theme.shadows[1], border: '1px solid', borderColor: alpha(theme.palette.divider, 0.1) }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Box>
                                    <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Completed</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>₹{stats.completed.amount.toLocaleString()}</Typography>
                                    <Typography variant="caption" color="text.secondary">{stats.completed.count} successful payouts</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main' }}><CheckCircle /></Avatar>
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>

                <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
                    {/* Status Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                        <Tabs
                            value={statusTab}
                            onChange={(_, val) => setStatusTab(val)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab label="All Payouts" sx={{ fontWeight: 700, py: 2 }} />
                            <Tab label="Requested" sx={{ fontWeight: 700, py: 2 }} />
                            <Tab label="Initiated" sx={{ fontWeight: 700, py: 2 }} />
                            <Tab label="In Progress" sx={{ fontWeight: 700, py: 2 }} />
                            <Tab label="Completed" sx={{ fontWeight: 700, py: 2 }} />
                        </Tabs>
                    </Box>

                    <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                            <TextField
                                fullWidth
                                placeholder="Search partners by name, email or ID..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.05),
                                        '& fieldset': { borderColor: alpha(theme.palette.divider, 0.1) },
                                        '&:hover fieldset': { borderColor: 'primary.light' },
                                        '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                                    }
                                }}
                            />
                        </Box>

                        {loading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                <CircularProgress size={32} thickness={5} />
                            </Box>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table sx={{ minWidth: 800 }}>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.04) }}>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Partner</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Amount</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Bank Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Account Number</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>IFSC Code</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Requested At</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {withdrawals.map((withdrawal) => (
                                                <TableRow key={withdrawal.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Avatar
                                                                src={withdrawal.tutor ? getAvatarUrl(withdrawal.tutor) : undefined}
                                                                sx={{
                                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                                    color: 'primary.main',
                                                                    fontWeight: 700,
                                                                    width: 40,
                                                                    height: 40,
                                                                }}
                                                            >
                                                                {(withdrawal.tutor?.tutorProfile?.name || 'U').charAt(0).toUpperCase()}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {withdrawal.tutor?.tutorProfile?.name || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {withdrawal.tutor?.email}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <AccountBalanceWallet sx={{ fontSize: '1rem', color: 'primary.main' }} />
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                ₹{withdrawal.amount.toLocaleString()}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <AccountBalance sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                                            <Typography variant="body2">
                                                                {withdrawal.tutor?.bankDetails?.bankName || <Typography variant="caption" color="text.disabled">Not Provided</Typography>}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {withdrawal.tutor?.bankDetails?.accountNumber ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                                                    {withdrawal.tutor.bankDetails.accountNumber}
                                                                </Typography>
                                                                <Tooltip title="Copy">
                                                                    <IconButton size="small" onClick={() => handleCopy(withdrawal.tutor!.bankDetails!.accountNumber)}>
                                                                        <ContentCopy sx={{ fontSize: '0.875rem' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="caption" color="text.disabled">—</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {withdrawal.tutor?.bankDetails?.ifscCode ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05), px: 1, borderRadius: 1 }}>
                                                                    {withdrawal.tutor.bankDetails.ifscCode}
                                                                </Typography>
                                                                <Tooltip title="Copy">
                                                                    <IconButton size="small" onClick={() => handleCopy(withdrawal.tutor!.bankDetails!.ifscCode)}>
                                                                        <ContentCopy sx={{ fontSize: '0.875rem' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="caption" color="text.disabled">—</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={
                                                                withdrawal.status === 'success' ? 'completed' :
                                                                    withdrawal.status === 'released' ? 'initiated' :
                                                                        withdrawal.status === 'inProgress' ? 'in-progress' :
                                                                            withdrawal.status === 'requested' ? 'requested' :
                                                                                withdrawal.status
                                                            }
                                                            color={
                                                                withdrawal.status === 'success' ? 'success' :
                                                                    withdrawal.status === 'rejected' ? 'error' :
                                                                        withdrawal.status === 'requested' ? 'info' :
                                                                            'warning'
                                                            }
                                                            size="small"
                                                            sx={{ fontWeight: 800, fontSize: '0.6rem', borderRadius: 1, textTransform: 'uppercase' }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                            {firebaseService.convertTimestamp(withdrawal.requestedAt).toLocaleDateString()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Tooltip title="View Details">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setSelectedRequest(withdrawal);
                                                                        setViewDialogOpen(true);
                                                                    }}
                                                                    sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
                                                                >
                                                                    <Visibility sx={{ fontSize: '1.25rem' }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {(withdrawal.status === 'pending' || withdrawal.status === 'requested' || withdrawal.status === 'released' || withdrawal.status === 'inProgress') && (
                                                                <Tooltip title={withdrawal.status === 'pending' || withdrawal.status === 'requested' ? "Process Payout" : "Update Status"}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleOpenPayout(withdrawal)}
                                                                        sx={{ color: 'primary.main', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
                                                                    >
                                                                        <Send sx={{ fontSize: '1.25rem' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {withdrawals.length === 0 && !loading && (
                                                <TableRow>
                                                    <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                                        <Typography variant="body1" color="text.secondary">No withdrawal requests found</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={total}
                                    page={page}
                                    onPageChange={(e, newPage) => setPage(newPage)}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                    sx={{ borderTop: 1, borderColor: 'divider' }}
                                />
                            </>
                        )}
                    </Box>
                </Paper>

                {/* View Payout Dialog */}
                <Dialog
                    open={viewDialogOpen}
                    onClose={() => setViewDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 4, boxShadow: theme.shadows[10] } }}
                >
                    <DialogTitle sx={{ fontWeight: 800, px: 3, pt: 3 }}>
                        Payout Details
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ fontWeight: 600, mt: 0.5 }}>
                            Request ID: #{selectedRequest?.id}
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ px: 3, pb: 3 }}>
                        <Stack spacing={3} divider={<Divider />}>
                            {/* Partner Info */}
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Partner Information
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar
                                        src={selectedRequest?.tutor ? getAvatarUrl(selectedRequest.tutor) : undefined}
                                        sx={{ width: 48, height: 48, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 700 }}
                                    >
                                        {(selectedRequest?.tutor?.tutorProfile?.name || 'U').charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                            {selectedRequest?.tutor?.tutorProfile?.name || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedRequest?.tutor?.email}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Bank Details */}
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Bank Account Details
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">Bank Name</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {selectedRequest?.accountDetails?.bankName || selectedRequest?.tutor?.bankDetails?.bankName || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">IFSC Code</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {selectedRequest?.accountDetails?.ifscCode || selectedRequest?.tutor?.bankDetails?.ifscCode || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ gridColumn: 'span 2' }}>
                                        <Typography variant="caption" color="text.secondary" display="block">Account Number</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                            {selectedRequest?.accountDetails?.accountNumber || selectedRequest?.tutor?.bankDetails?.accountNumber || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Payout Info */}
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Payout Information
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">Amount</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 800, color: 'primary.main' }}>₹{selectedRequest?.amount?.toLocaleString()}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                                        <Chip
                                            size="small"
                                            label={selectedRequest?.status}
                                            color={
                                                selectedRequest?.status === 'success' ? 'success' :
                                                    selectedRequest?.status === 'rejected' ? 'error' :
                                                        selectedRequest?.status === 'requested' ? 'info' :
                                                            'warning'
                                            }
                                            sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', height: 20 }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">Requested At</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {selectedRequest?.requestedAt ? firebaseService.convertTimestamp(selectedRequest.requestedAt).toLocaleString() : 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">Reference ID</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: selectedRequest?.transactionReference ? 'text.primary' : 'text.disabled' }}>
                                            {selectedRequest?.transactionReference || 'Not yet recorded'}
                                        </Typography>
                                    </Box>
                                    {selectedRequest?.adminNotes && (
                                        <Box sx={{ gridColumn: 'span 2' }}>
                                            <Typography variant="caption" color="text.secondary" display="block">Admin Notes</Typography>
                                            <Typography variant="body2" sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                                {selectedRequest.adminNotes}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 0 }}>
                        <Button onClick={() => setViewDialogOpen(false)} variant="outlined" fullWidth sx={{ borderRadius: 3, py: 1.5, fontWeight: 700 }}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Process Payout Dialog */}
                <Dialog
                    open={payoutDialogOpen}
                    onClose={handleClosePayout}
                    PaperProps={{
                        sx: { borderRadius: 3, width: '100%', maxWidth: 500 }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Process Withdrawal Request</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Update the status of the withdrawal request for <strong>{selectedRequest?.tutor?.tutorProfile?.name}</strong>.
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                            <TextField
                                label="Payout Amount (₹)"
                                fullWidth
                                type="number"
                                value={payoutAmount}
                                disabled // Prefilled from request, don't allow editing unless necessary
                                helperText={`Original Request Amount: ₹${selectedRequest?.amount || 0}`}
                            />

                            <TextField
                                label="Transaction Reference (ID)"
                                fullWidth
                                value={payoutRef}
                                onChange={(e) => setPayoutRef(e.target.value)}
                                placeholder="NEFT/UPI/Reference ID"
                            />

                            <TextField
                                select
                                label="Transfer Status"
                                fullWidth
                                value={payoutStatus}
                                onChange={(e) => setPayoutStatus(e.target.value as any)}
                            >
                                <MuiMenuItem value="released">Initiated (Released)</MuiMenuItem>
                                <MuiMenuItem value="inProgress">In Progress</MuiMenuItem>
                                <MuiMenuItem value="success">Completed (Success)</MuiMenuItem>
                            </TextField>

                            <TextField
                                label="Admin Notes"
                                fullWidth
                                multiline
                                rows={2}
                                value={payoutAdminNotes}
                                onChange={(e) => setPayoutAdminNotes(e.target.value)}
                            />

                            {payoutStatus === 'success' && (
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    Setting status to <strong>Completed</strong> will mark this request as successful. The tutor has already had the amount deducted from their wallet upon request creation.
                                </Alert>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 0 }}>
                        <Button onClick={handleClosePayout} sx={{ fontWeight: 600 }}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleProcessPayout}
                            disabled={isSubmitting || !payoutAmount || !payoutRef}
                            startIcon={isSubmitting ? <CircularProgress size={16} /> : <CheckCircle />}
                            sx={{ borderRadius: 2, fontWeight: 600, px: 3 }}
                        >
                            {isSubmitting ? 'Processing...' : 'Record Payout'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Layout>
    );
};

export default Payouts;
